import { DateTime } from "luxon";

import {
  defaultEquipmentIndicators,
  defaultEquipmentIndicatorsApartment,
  defaultEquipmentIndicatorsHouse,
  defaultEquipmentIndicatorsMultifamilyHouse,
  defaultModernizationsMatrix,
} from "@/components/refineRealEstate/dataRefineTabs/defaultModernizationEquipmentMatrix";
import {
  ActualUseEnum,
  BasementConversionEnum,
  BasementEnum,
  BuildingStandardDeterminationMethodEnum,
  Construction,
  HouseOrApartmentTypeEnum,
  ModernizationMethodEnum,
  ObjectTypeEnum,
  OutdoorFacilitiesEnum,
  OwnershipEnum,
  ProductTypeEnum,
  PropertyInformation,
  RentReferenceEnum,
  RoofEnum,
} from "@/types/refineRealEstate";

export const initialState: PropertyInformation = {
  generalInfo: {
    productType: ProductTypeEnum.lendingValue,
    objectType: ObjectTypeEnum.house,
    buildingType: undefined,
    ownership: OwnershipEnum.fullOwnership,
    valuationDate: DateTime.now(),
    selfUseSuitability: true,
    actualUse: ActualUseEnum.ownerOccupied,
    completionStatus: "completedFull",
  },
  plotData: {
    plotArea: undefined,
    completionStatus: "notStarted",
  },
  buildingData: {
    constructionYear: undefined,
    livingArea: undefined,
    garages: undefined,
    completionStatus: "notStarted",
  },
  energyAndPhotovoltaic: {
    energyCertificate: undefined,
    energyEfficiencyHouseStandard: undefined,
    issueDate: undefined,
    certificateType: undefined,
    finalEnergy: undefined,
    primaryEnergy: undefined,
    CO2Emissions: undefined,
    photovoltaicAvailable: false,
    constructionYear: undefined,
    annualPowerYield: undefined,
    completionStatus: "notStarted",
  },
  buildingStandard: {
    method: BuildingStandardDeterminationMethodEnum.detailed,
    completionStatus: "completedFull",
  },
  modernization: {
    method: ModernizationMethodEnum.automatic,
    matrix: defaultModernizationsMatrix,
    completionStatus: "completedFull",
  },
  financialAndRentalInfo: {
    rent: {
      value: undefined,
      reference: RentReferenceEnum.euroPerSquareMeter,
    },
    garageRent: {
      value: undefined,
      reference: RentReferenceEnum.euroPerSquareMeter,
    },
    parkingSpaceRent: {
      value: undefined,
      reference: RentReferenceEnum.euroPerSquareMeter,
    },
    completionStatus: "notStarted",
  },
  propertyImages: {
    completionStatus: "notStarted",
  },
};

export const initialStateHouse: PropertyInformation = {
  ...initialState,
  buildingData: {
    ...initialState.buildingData,
    numberOfFloors: 1,
    construction: Construction.massive,
    roof: RoofEnum.full,
    basement: BasementEnum.full,
    basementConversion: BasementConversionEnum.none,
    houseOrApartmentType: HouseOrApartmentTypeEnum.detachedHouse,
    outdoorFacilities: OutdoorFacilitiesEnum.average,
  },
  buildingStandard: {
    ...initialState.buildingStandard,
    matrix: {
      ...defaultEquipmentIndicators,
      ...defaultEquipmentIndicatorsHouse,
    },
  },
};

export const initialStateApartment: PropertyInformation = {
  ...initialState,
  generalInfo: {
    ...initialState.generalInfo,
    ownership: OwnershipEnum.apartmentOwnership,
  },
  buildingData: {
    ...initialState.buildingData,
    outdoorParkingSpaces: undefined,
    unitsNumber: 10,
    floorNumber: 0,
    garden: false,
    elevator: false,
  },
  buildingStandard: {
    ...initialState.buildingStandard,
    matrix: {
      ...defaultEquipmentIndicators,
      ...defaultEquipmentIndicatorsApartment,
    },
  },
};

export const initialStateMultifamilyHouse: PropertyInformation = {
  ...initialState,
  buildingData: {
    ...initialState.buildingData,
    numberOfFloors: 1,
    construction: Construction.massive,
    roof: RoofEnum.full,
    basement: BasementEnum.full,
    basementConversion: BasementConversionEnum.none,
    outdoorParkingSpaces: undefined,
    unitsNumber: 10,
    elevator: false,
  },
  buildingStandard: {
    ...initialState.buildingStandard,
    matrix: {
      ...defaultEquipmentIndicators,
      ...defaultEquipmentIndicatorsMultifamilyHouse,
    },
  },
};

export const initialStateByObjectType = (objectType: ObjectTypeEnum): PropertyInformation => {
  switch (objectType) {
    case ObjectTypeEnum.multiFamilyHouse:
    case ObjectTypeEnum.mixedUseProperty:
      return initialStateMultifamilyHouse;
    case ObjectTypeEnum.apartment:
      return initialStateApartment;
    case ObjectTypeEnum.house:
      return initialStateHouse;
    default:
      return initialState;
  }
};
